import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import TransactionList from '../../transaction-list/transaction-list';
import MyTransfers from 'containers/my-transfers/my-transfers';
import Cards, { CardsProp } from '../../../../../containers/cards/cards';

/**
 * Custom hook for managing tabs in CardsList component.
 *
 * Handles tab state, tab definitions, tab navigation,
 * and auto-reset to cards tab when a new card is added.
 *
 * @remarks
 * Extracted from CardsList component to reduce complexity.
 *
 * CRITICAL FIX: Uses useCallback + useRef pattern to prevent infinite loops
 * - Memoizes CardsComponent with useCallback (stable identity across renders)
 * - Stores unstable handleItemSelected callback in ref
 * - Prevents React from unmounting/remounting component on parent re-renders
 *
 * @param modalType - Type of modal context (account or individual)
 * @param contractNumber - Contract number for filtering
 * @param accountLevel - Account level for filtering
 * @param pendingTransferCount - Count of pending transfers for badge
 * @param cardReadAccess - User's read access permission for cards
 * @param isCardNumberAdded - Flag indicating if a new card was just added
 * @param handleItemSelected - Callback when a card is selected (unstable reference from parent)
 */
export const useCardsListTabs = ({
  modalType,
  contractNumber,
  accountLevel,
  pendingTransferCount,
  cardReadAccess,
  isCardNumberAdded,
  handleItemSelected,
}: {
  modalType?: string;
  contractNumber: string;
  accountLevel: string;
  pendingTransferCount: number;
  cardReadAccess: boolean;
  isCardNumberAdded: boolean;
  handleItemSelected: (item: any) => void;
}) => {
  // PATTERN: useRef for unstable callback (prevents infinite loops)
  // Store handleItemSelected in ref to avoid recreation cycles
  const handleItemSelectedRef = useRef(handleItemSelected);
  useEffect(() => {
    handleItemSelectedRef.current = handleItemSelected;
  });

  // PATTERN: useCallback for component functions (stable identity)
  // Memoize CardsComponent to prevent unmount/remount cycles
  const CardsComponent = useCallback(
    ({ modalType, contractNumber, accountLevel, onItemSelected }: CardsProp) => {
      return (
        <div className='cards'>
          <Cards
            modalType={modalType}
            onItemSelected={onItemSelected}
            contractNumber={contractNumber}
            accountLevel={accountLevel}
          />
        </div>
      );
    },
    [] // Empty deps - component function never changes
  );

  // PATTERN: useMemo for expensive arrays (prevents recreation)
  // pendingTransferCount must be in deps so the badge label stays current.
  // FIX #4: onItemSelected uses a stable lambda that forwards to the ref at call-time,
  //         not a snapshot of .current captured at memo-build time.
  const CardTabs = useMemo(
    () => [
      {
        id: 'cards',
        label: 'All',
        hasAccess: cardReadAccess,
        children: (
          <CardsComponent
            modalType={modalType}
            contractNumber={contractNumber}
            accountLevel={accountLevel}
            onItemSelected={(item: any) => handleItemSelectedRef.current(item)}
          />
        ),
      },
      {
        id: 'transactions',
        label: 'Transactions',
        hasAccess: cardReadAccess,
        children: <TransactionList />,
      },
      {
        id: 'transfers',
        label: `Pending transfers (${pendingTransferCount})`,
        hasAccess: cardReadAccess,
        children: <MyTransfers />,
      },
    ],
    [CardsComponent, cardReadAccess, modalType, contractNumber, accountLevel, pendingTransferCount]
  );

  const [defaultTab, setDefaultTab] = useState(() => CardTabs[0]);

  // FIX #5: Reset to cards tab ONLY when a new card is added.
  // CardTabs is intentionally NOT in deps: it changes on every pendingTransferCount update,
  // which would reset the active tab every time a transfer lands — a regression.
  // We set to CardTabs[0] directly only inside the isCardNumberAdded guard.
  useEffect(() => {
    if (isCardNumberAdded) {
      setDefaultTab(CardTabs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCardNumberAdded]); // CardTabs intentionally excluded — see comment above

  const handleTabClick = (tabItem: any) => {
    if (tabItem && tabItem.id === 'transactions') {
      window.dataLayer.push({
        event: 'Card_Portal_Transactions_Open_Report',
        button_click: true,
      });
    }
    setDefaultTab(tabItem);
  };

  return {
    CardTabs,
    defaultTab,
    handleTabClick,
    CardsComponent,
  };
};
